package dev.lingvo.ops.state

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import dev.lingvo.ops.api.OpsApiException
import dev.lingvo.ops.components.OpsLoad
import dev.lingvo.ops.components.opsFailure
import dev.lingvo.site.api.Session
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch

/** A successful read remains visible while the same query refreshes; an error never becomes empty. */
data class OpsResourceState<T>(
    val load: OpsLoad<T> = OpsLoad.Loading,
    val isRefreshing: Boolean = false,
    val refreshFailure: OpsApiException? = null,
)

private data class OpsResourceModel<K, T>(
    val key: K,
    val generation: Long = 0,
    val resource: OpsResourceState<T> = OpsResourceState(),
)

private sealed interface OpsResourceIntent<out K, out T> {
    data class Begin<K>(val key: K, val generation: Long) : OpsResourceIntent<K, Nothing>
    data class Loaded<K, T>(val key: K, val generation: Long, val value: T) : OpsResourceIntent<K, T>
    data class Failed<K>(val key: K, val generation: Long, val failure: OpsApiException) : OpsResourceIntent<K, Nothing>
    data class Cancelled<K>(val key: K, val generation: Long) : OpsResourceIntent<K, Nothing>
}

private fun <K, T> reduceResource(
    model: OpsResourceModel<K, T>,
    intent: OpsResourceIntent<K, T>,
): OpsResourceModel<K, T> = when (intent) {
    is OpsResourceIntent.Begin -> {
        val sameQuery = intent.key == model.key
        val previous = model.resource.load.takeIf { sameQuery && it is OpsLoad.Ready }
        model.copy(key = intent.key, generation = intent.generation,
            resource = OpsResourceState(previous ?: OpsLoad.Loading, isRefreshing = previous != null))
    }
    is OpsResourceIntent.Loaded -> if (intent.key == model.key && intent.generation == model.generation) {
        model.copy(resource = OpsResourceState(OpsLoad.Ready(intent.value)))
    } else model
    is OpsResourceIntent.Failed -> if (intent.key == model.key && intent.generation == model.generation) {
        val previous = model.resource.load
        val retryable = intent.failure.status == 0 || intent.failure.status == 408 ||
            intent.failure.status == 429 || intent.failure.status in 500..599
        model.copy(resource = if (previous is OpsLoad.Ready && retryable) {
            OpsResourceState(previous, refreshFailure = intent.failure)
        } else OpsResourceState(OpsLoad.Failed(intent.failure)))
    } else model
    is OpsResourceIntent.Cancelled -> if (intent.key == model.key && intent.generation == model.generation) {
        model.copy(resource = model.resource.copy(isRefreshing = false))
    } else model
}

/** Owns one cancellable read effect. Generations also refuse completions from non-cooperative loaders. */
class OpsResourceStore<K, T>(
    initialKey: K,
    parentScope: CoroutineScope,
    private val currentOwner: () -> Boolean = { true },
    private val onUnauthorized: () -> Unit = {},
    private val loader: suspend (K) -> T,
) {
    private val scope = CoroutineScope(parentScope.coroutineContext + SupervisorJob(parentScope.coroutineContext[Job]))
    private var model by mutableStateOf(OpsResourceModel<K, T>(initialKey))
    private var active: Job? = null
    private var closed = false

    val state: OpsResourceState<T> get() = model.resource

    fun load(key: K) {
        if (!ownsRequest()) return
        val generation = model.generation + 1
        dispatch(OpsResourceIntent.Begin(key, generation))
        active?.cancel()
        active = scope.launch {
            if (!ownsRequest()) return@launch
            try {
                dispatch(OpsResourceIntent.Loaded(key, generation, loader(key)))
            } catch (cancelled: CancellationException) {
                dispatch(OpsResourceIntent.Cancelled(key, generation))
                throw cancelled
            } catch (failure: Throwable) {
                val error = opsFailure(failure)
                if (error.status == 401 && ownsRequest() && key == model.key && generation == model.generation) onUnauthorized()
                dispatch(OpsResourceIntent.Failed(key, generation, error))
            }
        }
    }

    fun refresh() = load(model.key)

    fun close() {
        closed = true
        scope.cancel()
    }

    private fun dispatch(intent: OpsResourceIntent<K, T>) {
        if (ownsRequest()) model = reduceResource(model, intent)
    }

    private fun ownsRequest(): Boolean {
        if (closed) return false
        if (currentOwner()) return true
        // Retire the snapshot as well as its effect, even when the owner changed without a render.
        model = OpsResourceModel(model.key, model.generation + 1)
        close()
        return false
    }
}

/** Call inside OpsShell's allowed content: disposal closes requests when authority or route changes. */
@Composable
fun <K, T> rememberOpsResource(key: K, loader: suspend (K) -> T): OpsResourceStore<K, T> {
    val scope = rememberCoroutineScope()
    val owner = Session.requestIdentity
    // A changed identity/query gets a fresh Loading state before any content is rendered.
    val store = remember(key, owner) {
        OpsResourceStore(key, scope, currentOwner = { Session.requestIdentity == owner },
            onUnauthorized = { if (owner != null && Session.requestIdentity == owner) Session.logout() }, loader = loader)
    }
    LaunchedEffect(store) { store.load(key) }
    DisposableEffect(store) { onDispose { store.close() } }
    return store
}
